import React, { useState } from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import { FaRegEnvelope } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import { handleErrors } from "../../modules/handleErrors";
import AuthPageLayout, {
  AuthPrimaryButton,
  AuthSecondaryLink,
  AuthStatusIcon,
} from "./AuthPageLayout";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loader, setLoader] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setInlineError("");

    try {
      setLoader(true);
      const response = await apiClient.post("/send-reset/", { email });
      setLoader(false);

      if (response.status === 404) {
        setSubmitted(true);
        return;
      }

      if (response.status === 502) {
        const message =
          response.data?.error || "Failed to send password reset email.";
        setInlineError(message);
        return;
      }

      if (!response.ok) {
        if (response.data?.email) {
          handleErrors(response.data);
          return;
        }
        setInlineError(
          response.data?.error || "Unable to process your request. Please try again."
        );
        return;
      }

      setSubmitted(true);
    } catch {
      setLoader(false);
      toast.error("Something went wrong. Please try again later.");
    }
  };

  if (submitted) {
    return (
      <AuthPageLayout variant="sage">
        <AuthStatusIcon type="success" />
        <h5 className="mb-2">Check your email</h5>
        <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
          If an account exists for <strong>{email}</strong>, we&apos;ve sent a
          password reset link. The link expires after a short time, so use it
          soon.
        </p>
        <p className="mb-4" style={{ fontSize: "13px", color: "#868B96" }}>
          Didn&apos;t receive it? Check your spam folder or try again with the
          correct email address.
        </p>
        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
          <AuthPrimaryButton onClick={() => setSubmitted(false)}>
            Send again
          </AuthPrimaryButton>
          <AuthSecondaryLink to="/login">Back to login</AuthSecondaryLink>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      variant="sage"
      title="Forgot your password?"
      subtitle="Enter the email address linked to your account and we'll send you a link to reset your password."
    >
      <form onSubmit={onSubmit}>
        <div className="form-group mb-3 text-start">
          <label
            htmlFor="forgot-email"
            className="form-label mb-1"
            style={{ fontSize: "13px", color: "#4a4a4a" }}
          >
            Email address
          </label>
          <InputGroup>
            <InputGroup.Text
              className="position-absolute bg-white border-0 px-2"
              style={{
                zIndex: 10,
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <FaRegEnvelope style={{ color: "#868B96" }} />
            </InputGroup.Text>
            <FormControl
              required
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="form-control rounded-5 border-0 ps-5 pe-3 py-2 shadow-sm"
              style={{ height: "50px", fontSize: "13px" }}
            />
          </InputGroup>
        </div>

        {inlineError && (
          <p
            className="text-start mb-3"
            style={{ fontSize: "13px", color: "#C62828" }}
            role="alert"
          >
            {inlineError}
          </p>
        )}

        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center mt-4">
          <AuthPrimaryButton type="submit" disabled={loader}>
            {loader ? "Sending..." : "Send reset link"}
          </AuthPrimaryButton>
          <Link
            to="/login"
            style={{ color: "#000", fontSize: "14px", fontWeight: 500 }}
          >
            Back to login
          </Link>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ForgotPassword;
